import { useMemo } from "react";
import { MealPlanObject } from "../api/mealplans.api";
import { AccommodationObject } from "../../../accommodation/infrastructure/api/accommodation.api";
import { OccupationEntity } from "../../../occupation/domain/occupation.entity";

interface Option {
  ticker: string;
  translations: { locale: string; name: string }[];
}

export interface Options {
  accommodations: Option[] | undefined;
  occupations: Option[] | undefined;
  mealPlans: Option[] | undefined;
  conditions: Option[] | undefined;
}

const mapToOption = ({
  ticker,
  translationsList,
}: OccupationEntity | AccommodationObject | MealPlanObject) => {
  return {
    ticker,
    translations: translationsList.map(({ locale, name }) => ({ locale, name })),
  };
};

export function usePopulateMultiCheckboxList(
  accommodations?: AccommodationObject[],
  mealPlans?: MealPlanObject[],
  conditions?: any[],
  occupations?: OccupationEntity[],
  occupationType?: string
) {
  const hasAccommodationModel = useMemo(() => {
    return occupationType === "PAX_TYPE";
  }, [occupationType]);

  const getOccupations = useMemo(() => {
    if (hasAccommodationModel) {
      return occupations?.filter(({ ticker }) => ticker === "pax").map(mapToOption, [occupations]);
    }
    return occupations?.filter(({ ticker }) => ticker !== "pax").map(mapToOption, [occupations]);
  }, [hasAccommodationModel, occupations]);

  return useMemo(
    () => ({
      hasAccommodationModel,
      list: {
        accommodations: accommodations?.map(mapToOption),
        occupations: getOccupations,
        mealPlans: mealPlans?.map(mapToOption),
        conditions: conditions?.map(({ ticker, translations }) => ({
          ticker,
          translations: translations.map(({ locale, name }: { locale: string; name: string }) => ({
            locale,
            name,
          })),
        })),
      },
    }),
    [accommodations, hasAccommodationModel, getOccupations, mealPlans, conditions]
  );
}
